/**
 * ContextMenu.tsx - Keyboard-navigable context menu component
 * 
 * Features:
 * - Arrow key navigation
 * - Enter to select
 * - Escape to close
 * - Auto-focus on open
 * - Click outside to close
 */

import React, { useEffect, useRef, useState } from 'react';

export interface ContextMenuItem {
  id: string;
  label: string;
  command: string;
  shortcut?: string;
  icon?: string;
  divider?: boolean;
  disabled?: boolean;
  danger?: boolean;
  submenu?: ContextMenuItem[];
}

export interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onSelect: (command: string) => void;
  onClose: () => void;
}

export function ContextMenu({ x, y, items, onSelect, onClose }: ContextMenuProps) {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Filter out dividers and disabled items for navigation
  const navigableItems = items.filter(item => !item.divider && !item.disabled);

  // Focus first item on mount
  useEffect(() => {
    const firstNavigableIndex = items.findIndex(item => !item.divider && !item.disabled);
    if (firstNavigableIndex !== -1) {
      setFocusedIndex(firstNavigableIndex);
      itemRefs.current[firstNavigableIndex]?.focus();
    }
  }, [items]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          moveFocus(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          moveFocus(-1);
          break;
        case 'Enter':
        case 'ArrowRight':
          e.preventDefault();
          const item = items[focusedIndex];
          if (item && !item.divider && !item.disabled) {
            if (item.submenu) {
              setOpenSubmenu(item.id);
            } else {
              onSelect(item.command);
              onClose();
            }
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (openSubmenu) {
            setOpenSubmenu(null);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedIndex, items, onSelect, onClose]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Delay to avoid immediate close from the click that opened the menu
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Move focus to next/previous navigable item
  const moveFocus = (direction: 1 | -1) => {
    let newIndex = focusedIndex;
    
    do {
      newIndex += direction;
      
      // Wrap around
      if (newIndex < 0) {
        newIndex = items.length - 1;
      } else if (newIndex >= items.length) {
        newIndex = 0;
      }
      
      // Stop if we've looped back to start
      if (newIndex === focusedIndex) break;
      
    } while (items[newIndex]?.divider || items[newIndex]?.disabled);

    setFocusedIndex(newIndex);
    itemRefs.current[newIndex]?.focus();
  };

  const handleItemClick = (item: ContextMenuItem, index: number) => {
    if (item.divider || item.disabled) return;
    
    setFocusedIndex(index);
    
    if (item.submenu) {
      console.log('[ContextMenu] Toggling submenu:', item.id, 'currently open:', openSubmenu);
      setOpenSubmenu(openSubmenu === item.id ? null : item.id);
    } else {
      console.log('[ContextMenu] Executing command:', item.command);
      onSelect(item.command);
      onClose();
    }
  };

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: `${x}px`,
        top: `${y}px`,
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 10000,
        minWidth: '200px',
        padding: '4px 0'
      }}
      role="menu"
      aria-label="Context menu"
    >
      {items.map((item, index) => {
        if (item.divider) {
          return (
            <div
              key={`divider-${index}`}
              style={{
                height: '1px',
                backgroundColor: '#e0e0e0',
                margin: '4px 0'
              }}
              role="separator"
            />
          );
        }

        return (
          <button
            key={item.id}
            ref={el => itemRefs.current[index] = el}
            style={{
              position: 'relative',
              width: '100%',
              textAlign: 'left',
              padding: '8px 16px',
              border: 'none',
              background: focusedIndex === index ? '#f0f0f0' : 'none',
              cursor: item.disabled ? 'not-allowed' : 'pointer',
              color: item.danger ? '#d32f2f' : item.disabled ? '#999' : 'inherit',
              opacity: item.disabled ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={() => {
              if (!item.disabled) {
                setFocusedIndex(index);
                if (item.submenu) {
                  setOpenSubmenu(item.id);
                }
              }
            }}
            onMouseLeave={() => {
              // Don't close submenu on mouse leave - let click outside handle it
            }}
            onClick={() => handleItemClick(item, index)}
            disabled={item.disabled}
            role="menuitem"
            tabIndex={focusedIndex === index ? 0 : -1}
          >
            {item.icon && <span style={{ width: '16px' }}>{item.icon}</span>}
            <span style={{ flex: 1 }}>{item.label}</span>
            {item.shortcut && (
              <span style={{ opacity: 0.5, fontSize: '0.9em' }}>
                {item.shortcut}
              </span>
            )}
            {item.submenu && (
              <span style={{ marginLeft: '8px' }}>▸</span>
            )}
            
            {/* Submenu */}
            {item.submenu && openSubmenu === item.id && (
              <div
                style={{
                  position: 'absolute',
                  left: '100%',
                  top: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  minWidth: '180px',
                  padding: '4px 0',
                  marginLeft: '4px',
                  zIndex: 10001
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {item.submenu.map(subitem => (
                  <button
                    key={subitem.id}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 16px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    onClick={() => {
                      onSelect(subitem.command);
                      onClose();
                    }}
                  >
                    {subitem.icon && <span style={{ width: '16px' }}>{subitem.icon}</span>}
                    <span style={{ flex: 1 }}>{subitem.label}</span>
                  </button>
                ))}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
